import { JSX } from 'react';
import { SelectOption } from '@ardoq/select';
import {
  APIEntityType,
  BooleanOperator,
  Operator,
  QueryBuilderRuleValue,
} from '@ardoq/api-types';

export enum SearchContext {
  COMPONENT = 'component',
  REFERENCE = 'reference',
  WORKSPACE = 'workspace',
}

export const PERSPECTIVES_TO_ADVANCED_SEARCH_OPERATOR_REPLACEMENTS = new Map<
  Operator | string,
  Operator
>([
  [Operator.CONTAINS, Operator.CONTAINS_SUBSTRING],
  [Operator.NOT_CONTAINS, Operator.NOT_CONTAINS_SUBSTRING],
  [Operator.DO_NOT_USE_CONTAINS_PHRASE, Operator.CONTAINS_SUBSTRING],
  [Operator.DO_NOT_USE_NOT_CONTAINS_PHRASE, Operator.NOT_CONTAINS_SUBSTRING],
  ['contains_terms', Operator.CONTAINS_SUBSTRING],
  ['not_contains_terms', Operator.NOT_CONTAINS_SUBSTRING],
]);

export const isExclusivelyPerspectivesOperator = (
  operator: Operator | string
) => {
  return PERSPECTIVES_TO_ADVANCED_SEARCH_OPERATOR_REPLACEMENTS.has(operator);
};

// INTERNAL QUERY (it has uuid, and rules dosnt need to be valid - it can be partial)
export type InternalQuery = {
  uuid: string;
  condition: BooleanOperator;
  rules: InternalRow[];
};

export type InternalRow = InternalQuery | InternalRule;

export type InternalRule = {
  uuid: string;
  field?: string;
  operator?: Operator;
  value?: QueryBuilderRuleValue;
  targetEntityType?: APIEntityType.COMPONENT_TYPE | APIEntityType.COMPONENT;
  referenceTypeName?: string;
};

export type SuggestionsFetchFn = (
  searchPhrase: string
) => Promise<Pick<SelectOption<string>, 'label' | 'value' | 'description'>[]>;

export type AsyncSuggestionsLoaders = {
  loadComponentKeySuggestionsAsync: SuggestionsFetchFn;
  loadComponentSuggestionsAsync: SuggestionsFetchFn;
  loadParentComponentSuggestionsAsync: SuggestionsFetchFn;
  loadComponentSuggestionsIncludingCurrentComponentAsync: SuggestionsFetchFn;
  loadWorkspaceSuggestionsAsync: SuggestionsFetchFn;
  loadUserSuggestionsAsync: SuggestionsFetchFn;
  loadTagSuggestionsAsync: SuggestionsFetchFn;
  loadComponentTypeNameSuggestionsAsync: SuggestionsFetchFn;
  /**
   * Suggestions for the component type on the far side of a "has reference"
   * rule. Defaults to `loadComponentTypeNameSuggestionsAsync`; pass it only
   * when the referenced component may live outside the searched workspaces.
   */
  loadReferencedComponentTypeNameSuggestionsAsync?: SuggestionsFetchFn;
  loadReferenceTypeNameSuggestionsAsync: SuggestionsFetchFn;
  loadReferenceOIDSuggestionsAsync: SuggestionsFetchFn;
};

export type GetLabelAsyncFn = (id: string) => Promise<string | undefined>;

export type AsyncLabelLoaders = {
  getComponentKeyLabelAsync: GetLabelAsyncFn;
  getComponentLabelAsync: GetLabelAsyncFn;
  getWorkspaceLabelAsync: GetLabelAsyncFn;
  getUserLabelAsync: GetLabelAsyncFn;
  getReferenceLabelAsync: GetLabelAsyncFn;
};

export enum RuleNotificationType {
  WARNING = 'WARNING',
}

export type RuleNotificationParams = {
  message: JSX.Element;
  notificationType: RuleNotificationType;
};

export type GetRuleNotificationParams = (
  rule: InternalRule
) => RuleNotificationParams | undefined;
